'use client'

import { useState } from 'react'
import Link from 'next/link'
import config from '../../data/site-config.json'
import areasData from '../../data/suburb-list.json'
import SuburbAutocomplete from './SuburbAutocomplete'
import { trackQuoteSubmit, trackPhoneClick } from '../../lib/analytics'
import AvailabilityBadge from '../common/AvailabilityBadge'

const allSuburbs = Object.values(areasData.regions).flat().map(s => typeof s === 'string' ? s : s.name).sort()

export default function QuoteForm() {
    const [step, setStep] = useState(1)
    const [formData, setFormData] = useState({
        // Car Details
        make: '',
        model: '',
        year: '',
        condition: '',
        hasKeys: true,
        isRegistered: false,
        // Contact Info
        name: '',
        email: '',
        phone: '',
        streetAddress: '',
        suburb: '',
        // Preferences
        preferredTime: '',
        additionalNotes: ''
    })
    const [status, setStatus] = useState({ type: '', message: '' })
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
        setFormData({ ...formData, [e.target.name]: value })
    }

    const nextStep = () => setStep(step + 1)
    const prevStep = () => setStep(step - 1)

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)
        setStatus({ type: '', message: '' })

        try {
            const response = await fetch('/api/quote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            })

            if (response.ok) {
                trackQuoteSubmit({
                    make: formData.make,
                    model: formData.model,
                    year: formData.year,
                    condition: formData.condition,
                    suburb: formData.suburb,
                })
                setStatus({ type: 'success', message: 'Quote request received! We will call you within 30 minutes with your offer.' })
                setStep(4) // Move to confirmation
            } else {
                setStatus({ type: 'error', message: 'Something went wrong. Please try again or call us directly.' })
            }
        } catch (error) {
            setStatus({ type: 'error', message: 'Network error. Please call us directly.' })
        }

        setIsSubmitting(false)
    }

    const progressWidth = `${(step / 4) * 100}%`

    return (
        <div style={{
            background: 'white',
            padding: 'var(--spacing-lg)',
            borderRadius: 'var(--border-radius)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: '700px',
            margin: '0 auto'
        }}>
            {/* Progress Bar */}
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem'
                }}>
                    <span style={{ color: step >= 1 ? 'var(--primary)' : 'var(--gray-300)', fontWeight: step === 1 ? '600' : '400' }}>1. Car Details</span>
                    <span style={{ color: step >= 2 ? 'var(--primary)' : 'var(--gray-300)', fontWeight: step === 2 ? '600' : '400' }}>2. Your Info</span>
                    <span style={{ color: step >= 3 ? 'var(--primary)' : 'var(--gray-300)', fontWeight: step === 3 ? '600' : '400' }}>3. Preferences</span>
                    <span style={{ color: step >= 4 ? 'var(--primary)' : 'var(--gray-300)', fontWeight: step === 4 ? '600' : '400' }}>4. Done</span>
                </div>
                <div style={{ background: 'var(--gray-200)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ background: 'var(--primary)', width: progressWidth, height: '100%', transition: 'width 0.3s ease' }}></div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Step 1: Car Details */}
                {step === 1 && (
                    <div>
                        <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--primary)' }}>Tell Us About Your Vehicle</h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Make *</label>
                                <input
                                    type="text"
                                    name="make"
                                    value={formData.make}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g. Toyota, Ford, Holden"
                                    style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Model *</label>
                                <input
                                    type="text"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleChange}
                                    required
                                    placeholder="e.g. Corolla, Ranger, Commodore"
                                    style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Year</label>
                            <input
                                type="number"
                                name="year"
                                value={formData.year}
                                onChange={handleChange}
                                min="1950"
                                max="2026"
                                placeholder="e.g. 2015"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                            />
                        </div>

                        <div style={{ marginTop: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Condition *</label>
                            <select
                                name="condition"
                                value={formData.condition}
                                onChange={handleChange}
                                required
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem', background: 'white' }}
                            >
                                <option value="">Select condition...</option>
                                <option value="running">Running - Drives Fine</option>
                                <option value="running-issues">Running - Has Issues</option>
                                <option value="not-running">Not Running - Dead Engine</option>
                                <option value="accident-damaged">Accident Damaged</option>
                                <option value="flood-damaged">Flood Damaged</option>
                                <option value="scrap">Complete Scrap</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-md)' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input type="checkbox" name="hasKeys" checked={formData.hasKeys} onChange={handleChange} />
                                Has Keys
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                <input type="checkbox" name="isRegistered" checked={formData.isRegistered} onChange={handleChange} />
                                Currently Registered
                            </label>
                        </div>

                        <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'right' }}>
                            <button type="button" onClick={nextStep} className="btn btn-primary" style={{ padding: '0.8rem 2rem' }}>
                                Next: Your Info →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Contact Info */}
                {step === 2 && (
                    <div>
                        <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--primary)' }}>Your Contact Information</h3>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Your Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                autoComplete="name"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                            />
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Phone Number *</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                autoComplete="tel"
                                placeholder="0400 000 000"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                            />
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                autoComplete="email"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                            />
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Pickup Address *</label>
                            <input
                                type="text"
                                name="streetAddress"
                                value={formData.streetAddress}
                                onChange={handleChange}
                                required
                                placeholder="e.g. 123 Main Street"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem' }}
                            />
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Your Suburb *</label>
                            <SuburbAutocomplete
                                name="suburb"
                                value={formData.suburb}
                                onChange={(val) => setFormData({ ...formData, suburb: val })}
                                placeholder="Start typing your suburb..."
                            />
                        </div>

                        <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', justifyContent: 'space-between' }}>
                            <button type="button" onClick={prevStep} className="btn" style={{ padding: '0.8rem 2rem', background: 'var(--gray-200)' }}>
                                ← Back
                            </button>
                            <button type="button" onClick={nextStep} className="btn btn-primary" style={{ padding: '0.8rem 2rem' }}>
                                Next: Preferences →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Preferences */}
                {step === 3 && (
                    <div>
                        <h3 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--primary)' }}>Pickup Preferences</h3>

                        <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Preferred Pickup Time</label>
                            <select
                                name="preferredTime"
                                value={formData.preferredTime}
                                onChange={handleChange}
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem', background: 'white' }}
                            >
                                <option value="">As soon as possible</option>
                                <option value="today">Today</option>
                                <option value="tomorrow">Tomorrow</option>
                                <option value="this-week">This Week</option>
                                <option value="next-week">Next Week</option>
                                <option value="flexible">I'm Flexible</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-md)' }}>
                            <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Additional Notes</label>
                            <textarea
                                name="additionalNotes"
                                value={formData.additionalNotes}
                                onChange={handleChange}
                                rows="4"
                                style={{ width: '100%', padding: '0.8rem', border: '1.5px solid var(--gray-300)', borderRadius: 'var(--border-radius)', fontSize: '1rem', resize: 'vertical' }}
                                placeholder="Any additional information about the car or pickup location..."
                            />
                        </div>

                        {/* Summary */}
                        <div style={{ background: 'var(--gray-100)', padding: 'var(--spacing-md)', borderRadius: 'var(--border-radius)', marginBottom: 'var(--spacing-md)' }}>
                            <h4 style={{ marginBottom: 'var(--spacing-sm)' }}>Summary</h4>
                            <p><strong>Vehicle:</strong> {formData.year} {formData.make} {formData.model}</p>
                            <p><strong>Condition:</strong> {formData.condition}</p>
                            <p><strong>Location:</strong> {formData.streetAddress}, {formData.suburb}</p>
                            <p><strong>Contact:</strong> {formData.name} - {formData.phone}</p>
                        </div>

                        {status.message && (
                            <div style={{
                                padding: 'var(--spacing-sm)',
                                marginBottom: 'var(--spacing-sm)',
                                borderRadius: 'var(--border-radius)',
                                background: status.type === 'success' ? '#d4edda' : '#f8d7da',
                                color: status.type === 'success' ? '#155724' : '#721c24'
                            }}>
                                {status.message}
                            </div>
                        )}

                        <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', justifyContent: 'space-between' }}>
                            <button type="button" onClick={prevStep} className="btn" style={{ padding: '0.8rem 2rem', background: 'var(--gray-200)' }}>
                                ← Back
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ padding: '0.8rem 2rem', fontSize: '1.1rem', opacity: isSubmitting ? 0.7 : 1 }}>
                                {isSubmitting ? 'Submitting...' : '✓ Get My Free Quote'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 4: Confirmation */}
                {step === 4 && (
                    <div style={{ textAlign: 'center', padding: 'var(--spacing-lg)' }}>
                        <div style={{ fontSize: '4rem', marginBottom: 'var(--spacing-md)' }}>✅</div>
                        <h2 style={{ color: 'var(--primary)' }}>Quote Request Received!</h2>
                        <p style={{ fontSize: '1.2rem', marginBottom: 'var(--spacing-md)' }}>
                            Thank you, {formData.name}! We will call you at <strong>{formData.phone}</strong> within 30 minutes with your quote.
                        </p>
                        <div style={{ background: 'var(--gray-100)', padding: 'var(--spacing-md)', borderRadius: 'var(--border-radius)', marginBottom: 'var(--spacing-md)' }}>
                            <p><strong>Your Vehicle:</strong> {formData.year} {formData.make} {formData.model}</p>
                            <p><strong>Pickup Location:</strong> {formData.streetAddress}, {formData.suburb}</p>
                        </div>
                        <p>Can't wait? Call us now:</p>
                        <Link href={`tel:${config.business.phone}`} className="btn btn-primary" style={{ fontSize: '1.2rem', padding: '1rem 2rem' }} onClick={() => trackPhoneClick('quote_confirmation')}>
                            📞 {config.business.phone}
                        </Link>
                        <div style={{ marginTop: '0.75rem' }}>
                            <AvailabilityBadge size="sm" />
                        </div>
                    </div>
                )}
            </form>
        </div>
    )
}
